const API_BASE = 'https://bwbldxdrz7.execute-api.us-east-1.amazonaws.com/prod/api';
const API_BASE_ROOT = 'https://bwbldxdrz7.execute-api.us-east-1.amazonaws.com/prod';

export interface Threat {
  id: string;
  source: 'aws' | 'crowdstrike' | 'okta';
  type: string;
  severity: string;
  title: string;
  description: string;
  timestamp?: string;
  remediationStatus?: 'detected' | 'analyzing' | 'remediating' | 'remediated' | 'failed';
}

export interface AIAnalysisResult {
  threat: Threat;
  pipeline: {
    ml: { anomalyScore: number; factors: Record<string, any>; timestamp: string };
    dnn: { classification: string; confidence: number; mitreTactic: string; mitreId: string; mitreName: string; timestamp: string };
    genai: { action: string; commands: string[]; explanation: string; rollback: string; severity_assessment: string; model: string; auto_remediate?: boolean };
    totalTimeMs: number;
  };
  recommendation: { action: string; commands: string[]; confidence: number; autoExecute: boolean; autoRemediate?: boolean };
  remediation?: { success: boolean; actions: string[]; errors: string[] };
  status: 'pending' | 'remediated' | 'failed';
}

export interface DemoStatus {
  ec2: { instanceId: string; state: string };
  s3: { bucket: string; isPublic: boolean };
  findings: { count: number };
  remediations: number;
  remediatedIds?: string[];
}

export interface CrossDomainThreat {
  id: string;
  source: string;
  type: string;
  severity: string;
  title: string;
  description: string;
  timestamp: string;
  status: string;
  correlationId: string;
  indicators: Record<string, string>;
}

export interface CrossDomainAttack {
  id: string;
  scenario: {
    id: string;
    name: string;
    description: string;
  };
  context: {
    attackerIP: string;
    targetUser: string;
    instanceId: string;
    bucketName: string;
    hostname: string;
    deviceId: string;
    country: string;
    region: string;
    failedAttempts: number;
    timestamp: string;
  };
  threats: {
    okta: CrossDomainThreat;
    aws: CrossDomainThreat;
    crowdstrike: CrossDomainThreat;
  };
  correlation: {
    sharedIndicators: string[];
    attackerIP: string;
    targetUser: string;
    timespan: string;
    confidence: number;
  };
  mitre: {
    tactics: string[];
    techniques: string[];
    severity: string;
  };
  remediation: {
    okta: {
      source: string;
      actions: Array<{
        step: number;
        action: string;
        command: string;
        description: string;
        keywords: string[];
      }>;
    };
    aws: {
      source: string;
      actions: Array<{
        step: number;
        action: string;
        command: string;
        description: string;
        keywords: string[];
      }>;
    };
    crowdstrike: {
      source: string;
      actions: Array<{
        step: number;
        action: string;
        command: string;
        description: string;
        keywords: string[];
      }>;
    };
  };
  timestamp: string;
}

export const api = {
  health: () => fetch(API_BASE + '/health').then(r => r.json()),

  ai: {
    analyze: (threat: Threat, autoRemediate = false): Promise<AIAnalysisResult> =>
      fetch(API_BASE + '/ai/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...threat, autoRemediate })
      }).then(r => r.json()),
    
    remediate: (threat: Threat, genAIResult?: any): Promise<{ success: boolean; actions: string[]; errors: string[] }> =>
      fetch(API_BASE + '/ai/remediate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threat, genAIResult })
      }).then(r => r.json()),

    getStatus: (threatId?: string): Promise<any> =>
      fetch(API_BASE + '/ai/status' + (threatId ? '/' + threatId : '')).then(r => r.json()),
  },

  crossDomain: {
    generate: (): Promise<CrossDomainAttack> =>
      fetch(API_BASE + '/cross-domain/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }).then(r => r.json()),
    
    remediate: (remediation: any, context: any): Promise<{ success: boolean; results: any; summary: any }> =>
      fetch(API_BASE + '/cross-domain/remediate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remediation, context })
      }).then(r => r.json()),
  },

  demo: {
    getStatus: (): Promise<DemoStatus> => fetch(API_BASE + '/demo/status').then(r => r.json()),
    triggerFindings: () => fetch(API_BASE + '/demo/trigger-findings', { method: 'POST' }).then(r => r.json()),
    archiveAll: () => fetch(API_BASE + '/demo/archive-all', { method: 'POST' }).then(r => r.json()),
    resetEnvironment: () => fetch(API_BASE + '/demo/reset', { method: 'POST' }).then(r => r.json()),
    makeS3Public: () => fetch(API_BASE + '/demo/s3-make-public', { method: 'POST' }).then(r => r.json()),
    startEC2: () => fetch(API_BASE + '/demo/ec2-start', { method: 'POST' }).then(r => r.json()),
  },

  aws: {
    getFindings: () => fetch(API_BASE + '/aws/findings').then(r => r.json()),
    isolateEC2: (instanceId?: string) => 
      fetch(API_BASE + '/aws/remediate/ec2-isolate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instanceId })
      }).then(r => r.json()),
    blockS3Public: (bucketName?: string) =>
      fetch(API_BASE + '/aws/remediate/s3-block-public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucketName })
      }).then(r => r.json()),
  },

  crowdstrike: {
    getDetections: () => fetch(API_BASE + '/crowdstrike/detections').then(r => r.json()),
    getHosts: () => fetch(API_BASE + '/crowdstrike/hosts').then(r => r.json()),
    containHost: (deviceId: string) =>
      fetch(API_BASE + '/crowdstrike/remediate/contain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId })
      }).then(r => r.json()),
    liftContainment: (deviceId: string) =>
      fetch(API_BASE + '/crowdstrike/remediate/lift-containment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId })
      }).then(r => r.json()),
    closeDetections: (compositeIds: string[]) =>
      fetch(API_BASE + '/crowdstrike/remediate/close-detection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ compositeIds })
      }).then(r => r.json()),
    reopenDetections: (compositeIds: string[]) =>
      fetch(API_BASE + '/crowdstrike/remediate/reopen-detection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ compositeIds })
      }).then(r => r.json()),
    getAllDetections: () => 
      fetch(API_BASE + '/crowdstrike/all-detections').then(r => r.json()),
  },

  okta: {
    getLogs: () => fetch(API_BASE + '/okta/logs').then(r => r.json()),
    getThreats: () => fetch(API_BASE + '/okta/threats').then(r => r.json()),
    getUsers: () => fetch(API_BASE + '/okta/users').then(r => r.json()),
    suspendUser: (userId: string) =>
      fetch(API_BASE + '/okta/remediate/suspend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      }).then(r => r.json()),
    revokeSessions: (userId: string) =>
      fetch(API_BASE + '/okta/remediate/revoke-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      }).then(r => r.json()),
  },

  getThreatIntel: () => fetch(API_BASE_ROOT + "/threat-intel").then(r => r.json()),

  metrics: {
    get5D: (incidents: any[], mode: "aggregate" | "incident" = "aggregate", incident?: any) =>
      fetch(API_BASE + "/metrics/5d", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ incidents, mode, incident })
      }).then(r => r.json()),
  },
};

export default api;

// Real Attack Simulation API
export const realAttacks = {
  // Get current status of all demo resources
  getStatus: async () => {
    const response = await fetch(`${API_BASE}/api/attacks/status`);
    return response.json();
  },
  
  // AWS S3 Attacks
  s3Expose: async () => {
    const response = await fetch(`${API_BASE}/api/attacks/aws/s3-expose`, { method: 'POST' });
    return response.json();
  },
  s3Remediate: async () => {
    const response = await fetch(`${API_BASE}/api/attacks/aws/s3-remediate`, { method: 'POST' });
    return response.json();
  },
  
  // AWS Security Group Attacks
  sgOpen: async () => {
    const response = await fetch(`${API_BASE}/api/attacks/aws/sg-open`, { method: 'POST' });
    return response.json();
  },
  sgRemediate: async () => {
    const response = await fetch(`${API_BASE}/api/attacks/aws/sg-remediate`, { method: 'POST' });
    return response.json();
  },
  
  // Okta Attacks
  oktaBruteForce: async () => {
    const response = await fetch(`${API_BASE}/api/attacks/okta/brute-force`, { method: 'POST' });
    return response.json();
  },
  oktaLockUser: async () => {
    const response = await fetch(`${API_BASE}/api/attacks/okta/lock-user`, { method: 'POST' });
    return response.json();
  },
  oktaUnlockUser: async () => {
    const response = await fetch(`${API_BASE}/api/attacks/okta/unlock-user`, { method: 'POST' });
    return response.json();
  }
};
